import { Injectable } from "@angular/core";
import { CulturalActivityStatus } from "@common/ADAPT.Common.Model/organisation/cultural-activity-status";
import { Meeting, MeetingStatus } from "@common/ADAPT.Common.Model/organisation/meeting";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { UserService } from "@common/user/user.service";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { ActivityHandler } from "@org-common/lib/activities/activity-handler";
import { NonCulturalActivityType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { IMeetingTeamGroup, MeetingsService } from "@org-common/lib/meetings/meetings.service";
import moment from "moment";
import { lastValueFrom, merge } from "rxjs";
import { filter, map } from "rxjs/operators";
import { IActivityGroup } from "../activities-metadata";

@Injectable()
export class MeetingActivityHandler extends ActivityHandler<Meeting> {
    public readonly entityType = Meeting;
    public readonly activityTypes = [NonCulturalActivityType.Meetings];

    constructor(
        private meetingsService: MeetingsService,
        private rxjsBreezeService: RxjsBreezeService,
        private userService: UserService,
    ) {
        super();
    }

    public get shouldUpdate$() {
        return merge(
            this.rxjsBreezeService.entityTypeChanged(Meeting).pipe(
                filter((meeting) => meeting.meetingAttendees?.some((a) => a.attendeeId === this.userService.currentPerson?.personId) ?? false),
            ),
            this.meetingsService.meetingForMeetingAttendeeChangedForCurrentPerson$,
        ).pipe(
            map(() => [NonCulturalActivityType.Meetings]),
        );
    }

    public async getGroupedActivities(personId: number, startDate?: Date, endDate?: Date, excludeCompleted?: boolean) {
        const group = ActivitiesService.generateActivitiesGroupObject();

        const predicate = new MethodologyPredicate<Meeting>();

        if (excludeCompleted) {
            predicate.and(new MethodologyPredicate<Meeting>("status", "!=", MeetingStatus.Ended));
        }

        if (startDate) {
            predicate.and(new MethodologyPredicate<Meeting>("meetingDateTime", ">=", startDate));
        }

        if (endDate) {
            predicate.and(new MethodologyPredicate<Meeting>("meetingDateTime", "<=", endDate));
        }

        // sort all the meetings by the closest upcoming date
        const meetings = SortUtilities.sortArrayClosestDate(await lastValueFrom(this.meetingsService.getMeetingsForPersonByPredicate(personId, predicate)), (m) => m.meetingDateTime);

        const [upcomingMeetings, upcomingMeetingsPastScheduledDate] = ArrayUtilities.partition(meetings, (m) => moment(m.meetingDateTime).isSameOrAfter(new Date(), "date"));

        for (const meeting of upcomingMeetings.slice(0, 3)) {
            const status = getMeetingStatus(meeting);
            group[status].push(meeting);
        }

        for (const meeting of upcomingMeetingsPastScheduledDate.slice(0, 3)) {
            const status = getMeetingStatus(meeting);
            group[status].push(meeting);
        }

        group.allItems = meetings;
        group.totalCount = meetings.length;
        group.totalCountByStatus = meetings.reduce((counts, meeting) => {
            counts[getMeetingStatus(meeting)] += 1;
            return counts;
        }, ActivitiesService.generateActivitiesGroupedByStatusCountObject());

        return { [NonCulturalActivityType.Meetings]: group };

        function getMeetingStatus(meeting: Meeting) {
            const isOverdue = moment().startOf("day").isSameOrAfter(meeting.meetingDateTime) || meeting.extensions.isInProgress;
            return isOverdue ? CulturalActivityStatus.Overdue : CulturalActivityStatus.Upcoming;
        }
    }

    public groupActivitiesByStatus(group: IActivityGroup<Meeting>) {
        const overdue = this.meetingsService.groupMeetingsByTeam(group[CulturalActivityStatus.Overdue]).map(({
            team,
            meetings,
        }) => ({
            team,
            // sort by closest due date, so that soon upcoming meetings show first
            meetings: SortUtilities.sortArrayClosestDate(meetings, (m) => m.meetingDateTime),
        } as IMeetingTeamGroup));
        const upcoming = this.meetingsService.groupMeetingsByTeam(group[CulturalActivityStatus.Upcoming]).map(({
            team,
            meetings,
        }) => ({
            team,
            // sort by closest due date, so that soon upcoming meetings show first
            meetings: SortUtilities.sortArrayClosestDate(meetings, (m) => m.meetingDateTime),
        } as IMeetingTeamGroup));

        return {
            [CulturalActivityStatus.Overdue]: overdue,
            [CulturalActivityStatus.Upcoming]: upcoming,
        };
    }

    public async getCalendarEventForActivity(activity: Meeting) {
        return {
            title: (activity.extensions.isEnded ? "(Ended) " : "") + activity.name,
            startDate: activity.meetingDateTime,
            endDate: activity.endTime,
            completed: activity.extensions.isEnded,
        };
    }

}
